import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Monitor, ShieldCheck } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { APIError, http } from '@/services/api'

const REQUIRED_PARAMETERS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'nonce',
  'instance_id',
] as const

type Status = 'ready' | 'authorizing' | 'handoff' | 'error'

const readAuthorizationRequest = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search)
  const result: Record<string, string> = {}
  for (const name of REQUIRED_PARAMETERS) {
    const values = params.getAll(name)
    if (values.length !== 1 || !values[0]) {
      throw new Error('The desktop sign-in request is incomplete. Return to Ricald and try again.')
    }
    result[name] = values[0]
  }
  const unexpected = Array.from(params.keys()).find(
    (name) => !REQUIRED_PARAMETERS.includes(name as (typeof REQUIRED_PARAMETERS)[number]),
  )
  if (unexpected) {
    throw new Error('The desktop sign-in request contains unsupported data. Return to Ricald and try again.')
  }
  return result
}

export const DesktopAuthorizationPage = () => {
  const location = useLocation()
  const [status, setStatus] = useState<Status>('ready')
  const [error, setError] = useState('')
  const parsedRequest = useMemo(() => {
    try {
      return { body: readAuthorizationRequest(location.search), error: '' }
    } catch (requestError) {
      return {
        body: null,
        error: requestError instanceof Error ? requestError.message : 'Desktop sign-in could not start.',
      }
    }
  }, [location.search])
  const requestBody = parsedRequest.body

  const authorize = useCallback(async () => {
    if (!requestBody) return
    setStatus('authorizing')
    setError('')
    try {
      const payload = await http<{ callback_url: string }>('/auth/desktop/authorize', {
        method: 'POST',
        body: requestBody,
      })
      if (typeof payload.callback_url !== 'string') {
        throw new Error('This desktop sign-in request could not be verified. Return to Ricald and try again.')
      }
      setStatus('handoff')
      window.location.replace(payload.callback_url)
    } catch (requestError) {
      setStatus('error')
      setError(requestError instanceof APIError && requestError.status === 503
        ? 'Desktop sign-in is temporarily unavailable. Please try again.'
        : requestError instanceof Error
          ? requestError.message
          : 'Desktop sign-in could not finish.')
    }
  }, [requestBody])

  useEffect(() => {
    if (parsedRequest.error) {
      setStatus('error')
      setError(parsedRequest.error)
      return
    }
  }, [parsedRequest.error])

  return (
    <main className="min-h-screen bg-[var(--bg-canvas)] px-6 py-12 text-[var(--text-primary)]">
      <section className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
        <div className="w-full rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-8 shadow-sm sm:p-10">
          <div className="mb-8 flex items-center justify-between">
            <span className="text-lg font-semibold tracking-[-0.02em]">Ricald</span>
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--bg-elevated)]">
              <Monitor className="h-5 w-5" aria-hidden="true" />
            </span>
          </div>
          {status === 'error' ? (
            <>
              <AlertCircle className="mb-5 h-7 w-7 text-[var(--error)]" aria-hidden="true" />
              <h1 className="text-3xl font-semibold tracking-[-0.035em]">Desktop sign-in stopped</h1>
              <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]" role="alert">{error}</p>
              {requestBody && (
                <button
                  type="button"
                  className="mt-8 min-h-11 rounded-xl bg-[var(--text-primary)] px-5 text-base font-semibold text-[var(--bg-canvas)]"
                  onClick={() => void authorize()}
                >
                  Try again
                </button>
              )}
            </>
          ) : status === 'ready' ? (
            <>
              <ShieldCheck className="mb-5 h-7 w-7 text-[var(--accent)]" aria-hidden="true" />
              <h1 className="text-3xl font-semibold tracking-[-0.035em]">
                Continue to Ricald Desktop
              </h1>
              <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]">
                Continue only if you started this sign-in from the Ricald app on this computer.
                The browser will return a one-time code to that open app.
              </p>
              <button
                type="button"
                className="mt-8 min-h-11 rounded-xl bg-[var(--text-primary)] px-5 text-base font-semibold text-[var(--bg-canvas)]"
                onClick={() => void authorize()}
              >
                Continue to desktop
              </button>
            </>
          ) : (
            <>
              {status === 'handoff'
                ? <CheckCircle2 className="mb-5 h-7 w-7 text-[var(--success)]" aria-hidden="true" />
                : <Loader2 className="mb-5 h-7 w-7 animate-spin text-[var(--accent)]" aria-hidden="true" />}
              <h1 className="text-3xl font-semibold tracking-[-0.035em]">
                {status === 'handoff' ? 'Return to Ricald' : 'Connecting Ricald Desktop'}
              </h1>
              <p className="mt-4 text-base leading-7 text-[var(--text-secondary)]" aria-live="polite">
                {status === 'handoff'
                  ? 'Your identity is verified. This browser can be closed after the desktop app opens.'
                  : 'Verifying this one-time request with your signed-in workspace.'}
              </p>
            </>
          )}
          <p className="mt-10 border-t border-[var(--border-subtle)] pt-5 text-sm font-medium leading-6 text-[var(--text-tertiary)]">
            Ricald only returns a short-lived code to the loopback address owned by this desktop instance.
          </p>
        </div>
      </section>
    </main>
  )
}

export default DesktopAuthorizationPage
